import React, { useContext, useRef, useState, useEffect } from 'react';
import { CartContext } from '../../../context/CartContext';
import { X, Printer, UtensilsCrossed, Loader2 } from 'lucide-react';
import { DEFAULT_PRINTER_CONFIG, PRINT_SETTINGS } from '../../../config/printerConfig';
import printService from '../../../services/printService';
import { toast } from 'react-hot-toast';
import receiptConfigurationService from '../../../services/receiptConfigurationService';
import branchService from '../../../services/branchService';
import { generateKOTContent, DEFAULT_RECEIPT_CONFIG } from '../../../utils/receiptFormatter';

export default function KOTReceiptDisplay({ onClose, kotData, onConfirmPrint, isReprint }) {
  const { getCurrentOrder, currentTableId, currentSplitLabel, linkedWaiter } = useContext(CartContext);

  const receiptRef = useRef(null);
  // ⭐ Guards against double-firing the auto-print (e.g. React StrictMode double-invoke)
  const hasAutoPrinted = useRef(false);
  const currentOrder = getCurrentOrder();
  const currentTable = kotData?.table;

  // Receipt configuration state
  const [receiptConfig, setReceiptConfig] = useState(DEFAULT_RECEIPT_CONFIG);
  // Branch (restaurant) details state
  const [branchDetails, setBranchDetails] = useState(null);
  // Printing state
  const [isPrinting, setIsPrinting] = useState(false);

  // Fetch default receipt configuration and branch details
  useEffect(() => {
    const fetchReceiptConfig = async () => {
      try {
        const config = await receiptConfigurationService.getDefault('KOT');
        if (config) {
          setReceiptConfig(config);
        }
      } catch (error) {
        console.warn('Failed to fetch KOT receipt configuration, using defaults:', error);
      }
    };

    const fetchBranchDetails = async () => {
      try {
        const branch = await branchService.getCurrentBranch();
        if (branch) {
          setBranchDetails(branchService.formatBranchForReceipt(branch));
        }
      } catch (error) {
        console.warn('Failed to fetch branch details:', error);
      }
    };

    fetchReceiptConfig();
    fetchBranchDetails();
  }, []);

  // Group items by station - use kitchenStationName directly from API
  const groupItemsByStation = (items) => {
    const grouped = {};

    items.forEach(item => {
      // Use kitchenStationName directly from API (no conversion!)
      // Default to 'MAIN KITCHEN' only if null/undefined
      const stationName = item.kitchenStationName || 'MAIN KITCHEN';

      if (!grouped[stationName]) {
        grouped[stationName] = [];
      }
      grouped[stationName].push(item);
    });

    return grouped;
  };

  const stationGroups = groupItemsByStation(kotData?.items || []);
  const stations = Object.keys(stationGroups);

  const timestamp = new Date().toLocaleString('en-IN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  // 🖨️ Use printer configuration from config file
  const PRINTER_CONFIG = DEFAULT_PRINTER_CONFIG;

  /**
   * 🖨️ Direct Print to Thermal Printers (via backend)
   * Prints each station's KOT to its configured station-specific printer
   * Falls back to default KOT printer if no station printer is configured
   * Uses receipt configuration from database for formatting
   */
  const handleDirectPrint = async () => {
    try {
      // Print to each station's printer
      const printPromises = stations.map(async (station) => {
        const stationItems = stationGroups[station];

        // Generate formatted content using receipt configuration and branch details
        const kotPrintData = {
          restaurantName: branchDetails?.name || 'MAVERICK DINE',
          kotNumber: kotData?.kotNumber || 'N/A',
          tableName: kotData?.table?.name || currentTable?.name || 'N/A',
          tableSplit: currentSplitLabel,
          pax: currentOrder?.pax,
          stationName: station,
          waiterName: linkedWaiter?.name || null,
          timestamp: timestamp,
          items: stationItems.map(item => ({
            name: item.name,
            quantity: item.quantity,
            customization: item.customization,
          })),
          chefNotes: currentOrder?.orderNotes?.chefNotes,
        };

        // Generate formatted content based on receipt configuration
        // generateKOTContent returns { content, fontSize, kotNumber, station }
        const kotResult = generateKOTContent(receiptConfig, kotPrintData);
        const formattedContent = kotResult.content;

        // Use station-specific printing endpoint
        // This will route to station's printer if configured, otherwise fallback to default KOT printer
        const printPayload = {
          formattedContent,
          printType: 'KOT',
          kitchenStationName: station,
          content: {
            kotNumber: kotPrintData.kotNumber,
            tableName: kotPrintData.tableName,
            tableSplit: kotPrintData.tableSplit,
            pax: kotPrintData.pax,
            stationName: station,
            timestamp: kotPrintData.timestamp,
            items: kotPrintData.items,
            chefNotes: kotPrintData.chefNotes,
            allergies: kotPrintData.allergies,
          }
        };

        // Use printKOTToStation which routes to /api/print/kot/station/{stationName}
        return printService.printKOTToStation(station, printPayload);
      });

      const results = await Promise.all(printPromises);

      // Check if all prints succeeded
      const allSuccess = results.every(r => r?.success);
      const failedStations = results
        .filter(r => !r?.success)
        .map((r, idx) => stations[idx]);

      if (allSuccess) {
        toast.success(`KOTs printed to ${stations.length} station${stations.length > 1 ? 's' : ''} successfully!`, { duration: 2000 });
        return true;
      } else if (failedStations.length > 0) {
        toast.error(`KOT print failed for: ${failedStations.join(', ')}. Using browser print.`, { duration: 3000 });
        return false;
      }
      return true;
    } catch (error) {
      console.error('Direct KOT print error:', error);
      return false;
    }
  };

  // 🖨️ Enhanced Print Receipt for Thermal Printers - First sends KOT, then prints
  const handlePrint = async () => {
    setIsPrinting(true);

    // First, confirm and send the KOT to kitchen
    if (onConfirmPrint) {
      const success = onConfirmPrint();
      if (!success) {
        setIsPrinting(false);
        return; // Don't print if sending to kitchen failed
      }
    }

    // Try direct print first
    const directPrintSuccess = await handleDirectPrint();
    if (directPrintSuccess) {
      // Close modal immediately after successful direct print
      onClose();
      return;
    }

    // Fall back to browser print if direct print fails
    handleBrowserPrint();
    // Close after browser print dialog
    onClose();
  };

  // 🖨️ Browser-based print (fallback)
  const handleBrowserPrint = () => {
    // Generate HTML for each station receipt
    const receiptsHTML = stations.map((station) => {
      const items = stationGroups[station];

      return `
        <div class="kot-receipt" style="page-break-after: always;">
          <!-- Receipt Header -->
          <div class="header">
            <div class="company-name">${branchDetails?.name || 'MAVERICK DINE'}</div>
            <div class="company-subtitle">Kitchen Order Ticket</div>

            <!-- Station Badge -->
            <div class="station-badge-container">
              <span class="station-badge">${station}</span>
            </div>

            <div class="receipt-number">#${kotData?.kotNumber || 'N/A'}</div>
          </div>

          <!-- Table and Time Info -->
          <div class="info-section">
            <div class="info-row">
              <span class="font-bold">Table:</span>
              <span class="font-bold">${kotData?.table?.name || currentTable?.name || 'N/A'}${currentSplitLabel ? ` (${currentSplitLabel})` : ''}</span>
            </div>
            ${currentOrder?.pax > 0 ? `
            <div class="info-row">
              <span>Guests:</span>
              <span>${currentOrder.pax}</span>
            </div>
            ` : ''}
            <div class="info-row text-xs">
              <span>Date/Time:</span>
              <span>${timestamp}</span>
            </div>
          </div>

          <!-- Items -->
          <div class="items-header">
            <span>Item</span>
            <span>Qty</span>
          </div>

          ${items.map(item => `
            <div class="item-row">
              <div class="item-main">
                <span class="item-name">${item.name}</span>
                <span class="item-qty">${item.quantity}</span>
              </div>
              ${item.customization ? `
                <div class="customization-details">
                  ${item.customization.size ? `<div>Size: ${item.customization.size}</div>` : ''}
                  ${item.customization.spiceLevel ? `<div>Spice: ${item.customization.spiceLevel}</div>` : ''}
                  ${item.customization.preparation ? `<div>Prep: ${item.customization.preparation}</div>` : ''}
                  ${item.customization.quickNotes?.length > 0 ? `<div>Notes: ${item.customization.quickNotes.join(', ')}</div>` : ''}
                  ${item.customization.customInstructions ? `
                    <div class="custom-instructions">⚠️ ${item.customization.customInstructions}</div>
                  ` : ''}
                </div>
              ` : ''}
            </div>
          `).join('')}

          <!-- Chef Notes -->
          ${currentOrder?.orderNotes?.chefNotes ? `
          <div class="chef-notes">
            <div class="notes-label">👨‍🍳 CHEF NOTES:</div>
            <div class="notes-text">${currentOrder.orderNotes.chefNotes}</div>
          </div>
          ` : ''}

          <!-- Allergy Warnings -->
          ${currentOrder?.orderNotes?.allergies ? `
          <div class="allergy-alert">
            <div class="alert-label">⚠️ ALLERGY ALERT:</div>
            <div class="alert-text">${currentOrder.orderNotes.allergies}</div>
          </div>
          ` : ''}

          <!-- Footer -->
          <div class="footer">
            <div class="text-xs">
              Total Items: <span class="font-bold">${items.reduce((sum, item) => sum + item.quantity, 0)}</span>
            </div>
          </div>
        </div>
      `;
    }).join('');

    // Open print window with enhanced thermal printer CSS
    const printWindow = window.open('', '', 'width=800,height=600');

    // Check if popup was blocked
    if (!printWindow) {
      toast.error('Popup blocked! Please allow popups for this site, then try again.');
      return;
    }

    const printHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>KOT ${kotData?.kotNumber || 'N/A'} - ${currentTable?.name || ''}</title>
          <style>
            /* Reset and Base Styles */
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }

            /* Screen Preview Styles */
            @media screen {
              body {
                font-family: 'Courier New', Courier, monospace;
                background: #f5f5f5;
                padding: 20px;
              }

              .kot-receipt {
                background: white;
                width: ${PRINTER_CONFIG.width};
                max-width: 100%;
                padding: ${PRINTER_CONFIG.padding};
                margin-bottom: 20px;
                box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                border: 1px solid #ddd;
              }
            }

            /* Print Styles for Thermal Printer */
            @media print {
              @page {
                size: ${PRINTER_CONFIG.width} auto;
                margin: 0;
              }

              body {
                margin: 0;
                padding: 0;
                width: ${PRINTER_CONFIG.width};
                background: white;
              }

              .kot-receipt {
                width: 100%;
                padding: ${PRINTER_CONFIG.padding};
                ${PRINT_SETTINGS.enableAutoCut ? `padding-bottom: ${PRINT_SETTINGS.bottomMargin};` : ''}
                page-break-after: always;
              }

              .kot-receipt:last-child {
                page-break-after: auto;
              }

              /* Prevent page breaks inside items */
              .item-row {
                page-break-inside: avoid;
              }
            }

            /* Common Styles */
            body {
              font-family: 'Courier New', Courier, monospace;
              font-size: ${PRINTER_CONFIG.fontSize};
              line-height: ${PRINTER_CONFIG.lineHeight};
              color: #000;
            }

            /* Typography */
            .text-center { text-align: center; }
            .text-xs { font-size: 0.8em; }
            .font-bold { font-weight: bold; }

            /* Header */
            .header {
              text-align: center;
              margin-bottom: 1em;
              padding-bottom: 1em;
              border-bottom: 2px dashed #000;
            }

            .company-name {
              font-size: 1.4em;
              font-weight: bold;
              margin-bottom: 0.25em;
              letter-spacing: 0.05em;
            }

            .company-subtitle {
              font-size: 0.8em;
              margin-bottom: 0.5em;
            }

            .station-badge-container {
              margin: 0.75em 0;
            }

            .station-badge {
              background: #000;
              color: #fff;
              padding: 0.5em 1.5em;
              display: inline-block;
              font-weight: bold;
              font-size: 0.9em;
            }

            .receipt-number {
              font-size: 0.8em;
              margin-top: 0.5em;
            }

            /* Info Section */
            .info-section {
              margin-bottom: 1em;
              padding-bottom: 0.75em;
              border-bottom: 1px solid #000;
            }

            .info-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 0.25em;
              font-size: 0.9em;
            }

            /* Items */
            .items-header {
              display: flex;
              justify-content: space-between;
              font-weight: bold;
              margin-bottom: 0.5em;
              padding-bottom: 0.5em;
              border-bottom: 1px solid #000;
              font-size: 0.9em;
            }

            .items-header span:nth-child(1) { flex: 1; }
            .items-header span:nth-child(2) { width: 3em; text-align: center; }

            .item-row {
              margin-bottom: 0.75em;
            }

            .item-main {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 0.2em;
            }

            .item-name {
              flex: 1;
              font-weight: bold;
            }

            .item-qty {
              width: 3em;
              text-align: center;
              font-weight: bold;
            }

            .customization-details {
              font-size: 0.8em;
              color: #333;
              margin-left: 0.5em;
              margin-top: 0.25em;
            }

            .customization-details div {
              margin-bottom: 0.15em;
            }

            .custom-instructions {
              font-weight: bold;
              color: #b45309;
              margin-top: 0.25em;
            }

            /* Notes and Alerts */
            .chef-notes {
              background: #fef3c7;
              border: 1px solid #d97706;
              padding: 0.5em;
              margin: 0.75em 0;
              border-radius: 3px;
            }

            .notes-label {
              font-size: 0.8em;
              font-weight: bold;
              color: #92400e;
              margin-bottom: 0.25em;
            }

            .notes-text {
              font-size: 0.8em;
              color: #92400e;
            }

            .allergy-alert {
              background: #fee2e2;
              border: 1px solid #dc2626;
              padding: 0.5em;
              margin: 0.75em 0;
              border-radius: 3px;
            }

            .alert-label {
              font-size: 0.8em;
              font-weight: bold;
              color: #7f1d1d;
              margin-bottom: 0.25em;
            }

            .alert-text {
              font-size: 0.8em;
              color: #7f1d1d;
              font-weight: bold;
            }

            /* Footer */
            .footer {
              border-top: 2px dashed #000;
              padding-top: 0.75em;
              margin-top: 1em;
              text-align: center;
            }
          </style>
        </head>
        <body>
          ${receiptsHTML}

          <script>
            // Auto-print on load
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, ${PRINT_SETTINGS.autoPrintDelay});
            };

            // Close window after print or cancel
            ${PRINT_SETTINGS.autoCloseAfterPrint ? `
            window.onafterprint = function() {
              setTimeout(function() {
                window.close();
              }, ${PRINT_SETTINGS.autoCloseDelay});
            };
            ` : ''}
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(printHTML);
    printWindow.document.close();
  };

  // ⭐ NEW: Reprint mode — skip the confirmation/preview modal entirely
  // and fire the print immediately, in the background, once stations are ready.
  useEffect(() => {
    if (isReprint && !hasAutoPrinted.current && stations.length > 0) {
      hasAutoPrinted.current = true;
      handlePrint();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReprint, stations.length]);

  // ⭐ NEW: When reprinting, never render the modal UI — printing happens silently.
  if (isReprint) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="bg-gradient-to-r from-teal-700 to-teal-800 text-white p-3 flex justify-between items-center flex-shrink-0">
          <div className="flex items-center gap-2">
            <UtensilsCrossed size={18} />
            <h3 className="text-base font-bold">Kitchen Order Tickets</h3>
          </div>
          <button
            onClick={onClose}
            disabled={isPrinting}
            className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X size={20} />
          </button>
        </div>

        {/* Receipts Container - Full Width */}
        <div className="flex-1 p-4 bg-slate-100 overflow-y-auto">
          <div ref={receiptRef} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {stations.map((station, idx) => {
              const items = stationGroups[station];

              return (
                <div key={idx} className="bg-white p-4 rounded-lg font-mono text-sm border border-slate-200 shadow-md">
                  {/* Receipt Header */}
                  <div className="text-center border-b-2 border-dashed border-slate-300 pb-4 mb-4">
                    {receiptConfig.showRestaurantName && (
                      <h1 className="text-xl font-bold text-slate-900">{branchDetails?.name || 'MAVERICK DINE'}</h1>
                    )}
                    <p className="text-xs text-slate-600">Kitchen Order Ticket</p>

                    {/* Station Badge */}
                    {receiptConfig.showKitchenStation && (
                      <div className="my-3">
                        <span className="bg-slate-900 text-white px-4 py-2 text-sm font-bold inline-block">
                          {station}
                        </span>
                      </div>
                    )}

                    {receiptConfig.showOrderNumber && (
                      <p className="text-xs text-slate-600 mt-2">
                        #{kotData?.kotNumber || 'N/A'}
                      </p>
                    )}
                  </div>

                  {/* Table and Time Info */}
                  <div className="border-b border-slate-300 pb-3 mb-3">
                    {receiptConfig.showTableNumber && (
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-semibold text-slate-900">Table:</span>
                        <span className="font-bold text-slate-900">
                          {kotData?.table?.name || currentTable?.name || 'N/A'}
                          {currentSplitLabel && ` (${currentSplitLabel})`}
                        </span>
                      </div>
                    )}
                    {currentOrder?.pax > 0 && (
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-semibold text-slate-900">Guests:</span>
                        <span className="font-bold text-slate-900">{currentOrder.pax}</span>
                      </div>
                    )}
                    {receiptConfig.showTimestamp && (
                      <div className="flex justify-between text-xs text-slate-600">
                        <span>Date/Time:</span>
                        <span>{timestamp}</span>
                      </div>
                    )}
                  </div>

                  {/* Items Table */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs font-bold text-slate-900 mb-2 pb-2 border-b border-slate-300">
                      <span>Item</span>
                      <span>Qty</span>
                    </div>
                    {items.map((item, itemIdx) => (
                      <div key={itemIdx} className="mb-3">
                        <div className="flex justify-between text-xs text-slate-900">
                          <div className="flex-1">
                            <div className="font-semibold">{item.name}</div>
                            {/* Customizations */}
                            {item.customization && (
                              <div className="text-xs text-slate-600 mt-1 space-y-0.5">
                                {item.customization.size && (
                                  <div>Size: {item.customization.size}</div>
                                )}
                                {item.customization.spiceLevel && (
                                  <div>Spice: {item.customization.spiceLevel}</div>
                                )}
                                {item.customization.preparation && (
                                  <div>Prep: {item.customization.preparation}</div>
                                )}
                                {item.customization.quickNotes?.length > 0 && (
                                  <div>Notes: {item.customization.quickNotes.join(', ')}</div>
                                )}
                                {item.customization.customInstructions && (
                                  <div className="font-semibold text-amber-900 mt-1">
                                    ⚠️ {item.customization.customInstructions}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <span className="w-12 text-center font-bold">{item.quantity}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Chef Notes */}
                  {receiptConfig.showSpecialInstructions && currentOrder?.orderNotes?.chefNotes && (
                    <div className="bg-amber-50 border border-amber-300 rounded p-3 mb-3">
                      <div className="text-xs font-bold text-amber-900 mb-1">
                        👨‍🍳 CHEF NOTES:
                      </div>
                      <div className="text-xs text-amber-900">
                        {currentOrder.orderNotes.chefNotes}
                      </div>
                    </div>
                  )}

                  {/* Allergy Warnings */}
                  {receiptConfig.showSpecialInstructions && currentOrder?.orderNotes?.allergies && (
                    <div className="bg-red-50 border border-red-300 rounded p-3 mb-3">
                      <div className="text-xs font-bold text-red-900 mb-1">
                        ⚠️ ALLERGY ALERT:
                      </div>
                      <div className="text-xs text-red-900 font-semibold">
                        {currentOrder.orderNotes.allergies}
                      </div>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="border-t-2 border-dashed border-slate-300 pt-3 text-center">
                    <div className="text-xs text-slate-600">
                      Total Items: <span className="font-bold text-slate-900">{items.reduce((sum, item) => sum + item.quantity, 0)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white border-t-2 border-slate-200 p-3 flex gap-2 flex-shrink-0">
          <button
            onClick={handlePrint}
            disabled={isPrinting}
            className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 bg-teal-700 text-white rounded-lg text-sm font-medium hover:bg-teal-800 transition shadow-md disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isPrinting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Printing...
              </>
            ) : (
              <>
                <Printer size={16} />
                Print All KOTs
              </>
            )}
          </button>

          <button
            onClick={onClose}
            disabled={isPrinting}
            className="flex-1 px-5 py-2.5 bg-white border-2 border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 hover:border-slate-400 transition shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}